import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Search, FileText } from "lucide-react";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorMessage from "../../components/ui/ErrorMessage";
import { Input, Textarea } from "../../components/ui/Input";
import { useCreateTemplate, useTemplates } from "../../hooks/useTemplates";
import { ActionTemplateCreate } from "../../types/template";

export default function TemplatesPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [templateType, setTemplateType] = useState<string | "">("");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<ActionTemplateCreate>({
    template_type: "email",
    name: "",
    content: "",
    subject: "",
    provider_template_code: "",
    placeholder_params: {},
    delayed_send_minutes: undefined,
  });

  const { data, isLoading, isError, refetch } = useTemplates({
    page,
    page_size: 12,
    search: search || undefined,
    template_type: templateType || undefined,
  });
  const createMutation = useCreateTemplate();

  const items = data?.templates || [];
  const totalPages = data?.total_pages || 1;

  const openCreate = () => {
    setFormData({
      template_type: "email",
      name: "",
      content: "",
      subject: "",
      provider_template_code: "",
      placeholder_params: {},
      delayed_send_minutes: undefined,
    });
    setShowForm(true);
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    await createMutation.mutateAsync(formData);
    setShowForm(false);
    refetch();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Templates
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage message templates for email and SMS
          </p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          New Template
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <Input
            type="text"
            placeholder="Search templates..."
            variant="shadow-sm"
            showLabel={false}
            className="pl-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={templateType}
          onChange={(e) => setTemplateType(e.target.value)}
          className="px-3 py-2 border rounded"
        >
          <option value="">All Types</option>
          <option value="email">Email</option>
          <option value="sms">SMS</option>
        </select>
        <button
          onClick={() => {
            setPage(1);
            refetch();
          }}
          className="px-3 py-2 bg-gray-100 border rounded"
        >
          Apply
        </button>
      </div>

      {/* List */}
      <div>
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        ) : isError ? (
          <ErrorMessage
            message="Failed to load templates"
            onRetry={() => refetch()}
            className="py-12"
          />
        ) : (
          <div className="space-y-4">
            {items.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No templates found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {search
                    ? "Try adjusting your search."
                    : "Create a template to get started."}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((row: any) => (
                  <Link
                    key={row.id}
                    to={`/templates/${row.id}/edit`}
                    className="block bg-white shadow rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200"
                  >
                    <div className="px-4 py-5 sm:p-6">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-blue-500" />
                          <span className="text-lg font-medium text-gray-900 truncate">
                            {row.name}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-800 text-xs font-medium">
                            {row.template_type}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-sm text-gray-600">
                          {row.subject && (
                            <span className="truncate">
                              Subject: {row.subject}
                            </span>
                          )}
                          {row.provider_template_code && (
                            <span className="truncate">
                              Provider: {row.provider_template_code}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex justify-end items-center gap-2">
        <button
          className="px-3 py-1 bg-gray-100 rounded"
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
        >
          Prev
        </button>
        <span className="text-sm">
          Page {page} / {totalPages}
        </span>
        <button
          className="px-3 py-1 bg-gray-100 rounded"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </button>
      </div>

      {/* Create Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Create Template</h2>
              <form onSubmit={submitForm} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <select
                      value={formData.template_type}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          template_type: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border rounded"
                    >
                      <option value="email">email</option>
                      <option value="sms">sms</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject (email)
                    </label>
                    <input
                      type="text"
                      value={formData.subject || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, subject: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Provider Template Code
                    </label>
                    <input
                      type="text"
                      value={formData.provider_template_code || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          provider_template_code: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Textarea
                      label="Placeholder Params (JSON)"
                      value={JSON.stringify(
                        formData.placeholder_params || {},
                        null,
                        2,
                      )}
                      onChange={(e) => {
                        try {
                          const obj = JSON.parse(e.target.value || "{}");
                          setFormData({ ...formData, placeholder_params: obj });
                        } catch {}
                      }}
                      variant="rounded-md"
                      className="font-mono"
                      rows={4}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Delayed Send Minutes
                    </label>
                    <input
                      type="number"
                      value={formData.delayed_send_minutes ?? ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          delayed_send_minutes: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        })
                      }
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                </div>

                <div>
                  <Textarea
                    label="Content"
                    value={formData.content || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, content: e.target.value })
                    }
                    variant="rounded-md"
                    rows={8}
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 bg-gray-100 border rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
